import classNames from "classnames";
import { IWebsiteBuilderElementBase, IWebsiteBuilderElementDataBase, TWebsiteBuilderElement } from "./websiteBuilderElement";
import { websiteBuilderNormalizeValue, websiteBuilderNormalizeDatabaseId } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { IWebsiteBuilderNodeDesignStyles, TWebsiteBuilderNodeDatasetFromModel } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";

export interface IWebsiteBuilderVideoSettings {
  mediaId: number;
  src: string;
  autoPlay: boolean;
  muted: boolean;
  loop: boolean;
  description: string;
}

interface IWebsiteBuilderVideoData extends IWebsiteBuilderElementDataBase {
  type: "video";
  settings: IWebsiteBuilderVideoSettings;
}

export interface IWebsiteBuilderVideo extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderVideoData;
}

type TWebsiteBuilderVideoDatasetModel = Pick<IWebsiteBuilderVideoSettings, "src">;
export type TWebsiteBuilderVideoDataset = TWebsiteBuilderNodeDatasetFromModel<TWebsiteBuilderVideoDatasetModel>;

export function isWebsiteBuilderVideo(node: TWebsiteBuilderElement): node is IWebsiteBuilderVideo {
  return node?.element?.type === "video";
}

function generateDefaultVideoSettings(mediaId: number, src: string): IWebsiteBuilderVideoSettings {
  return {
    mediaId,
    src,
    autoPlay: false,
    muted: false,
    loop: false,
    description: "",
  };
}

export function websiteBuilderGenerateDefaultVideo(mediaId: number, src: string): IWebsiteBuilderVideo {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "video",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 320, height: 180 }, // 16:9
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 320, height: 180 }, // 16:9
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 288, height: 162 }, // 16:9
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultVideoSettings(mediaId, src),
    },
  };
}

export function websiteBuilderNormalizeVideoSettings(partialSettings: Partial<IWebsiteBuilderVideoSettings>): IWebsiteBuilderVideoSettings {
  const defaultSettings = generateDefaultVideoSettings(null, null);

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { mediaId, src, autoPlay, muted, loop, description } = partialSettings;

  const normalizedAutoPlay = websiteBuilderNormalizeValue({
    type: "boolean",
    value: autoPlay,
    defaultValue: defaultSettings.autoPlay,
    required: true,
  });

  const normalizedVideoSettings: IWebsiteBuilderVideoSettings = {
    mediaId: websiteBuilderNormalizeDatabaseId(mediaId, defaultSettings.mediaId, false),
    src: websiteBuilderNormalizeValue({
      type: "string",
      value: src,
      defaultValue: defaultSettings.src,
      required: false,
    }),
    autoPlay: normalizedAutoPlay,
    muted: normalizedAutoPlay || websiteBuilderNormalizeValue({
      type: "boolean",
      value: muted,
      defaultValue: defaultSettings.muted,
      required: true,
    }),
    loop: websiteBuilderNormalizeValue({
      type: "boolean",
      value: loop,
      defaultValue: defaultSettings.loop,
      required: true,
    }),
    description: websiteBuilderNormalizeValue({
      type: "string",
      value: description,
      defaultValue: defaultSettings.description,
      required: true,
    }),
  };

  return normalizedVideoSettings;
}

interface IWebsiteBuilderVideoDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
}

export function websiteBuilderGenerateVideoDesignStyles(settings: IWebsiteBuilderVideoSettings): IWebsiteBuilderVideoDesignStyles {
  return {
    containerClassName: classNames("website-builder-video-container", {
      "no-src-border": !settings.src,
    }),
    className: "website-builder-video",
    styleProperties: {},
    styleDeclaration: "",
  };
}
